import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import { appendAll, clear } from "../util/Utils";
import { FilterSorting } from "./FilterSorting";
import { FilterAccordion } from "./FilterAccordion";
import { Filter } from "./Filter";
import { events } from "../events";

const DESKTOP_FILTERS_CLASS = "find_filterList";
const DENSE_FILTERS_CLASS = "heureka_denseList";
const DENSE_FILTERS_SELECTOR = `.${DENSE_FILTERS_CLASS}`;
const EXPANDED_FILTERS_CLASS = "heureka_expandedList";
const EXPANDED_FILTERS_SELECTOR = `.${EXPANDED_FILTERS_CLASS}`;

export class FilterAccordions {
  private static readonly factory = HeurekaElementFactory.byClass(DESKTOP_FILTERS_CLASS, FilterAccordions);

  /*               */
  constructor(
    private readonly accordionsWrapper: HTMLElement,
    readonly accordions: FilterAccordion[] = FilterAccordion.factory.all(accordionsWrapper),
  ) {}

  /*                  */

  static template(rootElement?: ParentNode | null) {
    return FilterAccordions.factory.pick(undefined, rootElement);
  }

  /*               */

  static register() {
    events.onFilterSectionLoaded(FilterAccordions.initAll);
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    FilterAccordions.template(rootElement)?.init();
  }

  protected init() {
    this.updateFeaturesPositions();
    return this;
  }

  /*                       */

  public get values(): HTMLElement[] {
    return this.accordions.map((filterAccordion) => filterAccordion.accordion);
  }

  private get denseFiltersWrapper(): HTMLElement | null {
    return this.accordionsWrapper.querySelector(DENSE_FILTERS_SELECTOR);
  }

  private set denseModeValues(accordions: HTMLElement[]) {
    const destination = this.denseFiltersWrapper;
    if (destination) {
      clear(destination);
      appendAll(accordions, destination);
    }
  }

  private get expandedFiltersWrapper(): HTMLElement | null {
    return this.accordionsWrapper.querySelector(EXPANDED_FILTERS_SELECTOR);
  }

  private set expandedModeValues(accordions: HTMLElement[]) {
    const { expandedFiltersWrapper: destination } = this;
    if (destination) {
      clear(destination);
      appendAll(accordions, destination);
    }
  }

  get filters(): Filter[] {
    return this.accordions.map((accordion) => accordion.filter).filter((filter) => !!filter) as Filter[];
  }

  updateFeaturesPositions() {
    this.filters
      .map((filter) => filter.clearPosition())
      .forEach((filter: Filter, index) => {
        filter.position = index + 1;
      });
  }

  private filterAccordionsByMode(sortedAccordions: FilterAccordion[], isDenseMode: boolean): FilterAccordion[] {
    return sortedAccordions.filter((acc) => acc.filter && acc.filter.isDenseModeFilter === isDenseMode);
  }

  sortValues() {
    const sortedAccordions = FilterSorting.sort(this.accordions);
    const denseAccordions = this.filterAccordionsByMode(sortedAccordions, true);
    const expandedAccordions = this.filterAccordionsByMode(sortedAccordions, false);
    if (denseAccordions && denseAccordions.length > 0) {
      this.denseModeValues = denseAccordions.map((acc) => acc.accordion);
    }
    if (expandedAccordions && expandedAccordions.length > 0) {
      this.expandedModeValues = expandedAccordions.map((acc) => acc.accordion);
    }
  }
}
